import React, { useState, useEffect } from 'react'
import DashboardStats from '../components/dashboard/DashboardStats'
import RevenueChart from '../components/dashboard/RevenueChart'
import TopProducts from '../components/dashboard/TopProducts'
import OrderStatusChart from '../components/dashboard/OrderStatusChart'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { OrderStatus, OrderResponse } from '../types/orders'
import orderService from '../services/orderService'
import revenueService from '../services/revenueService'
import { RevenueStats, DailyRevenue } from '../types/revenueStats'
import { TopSellingItem } from '../types/topSelling'
import { format } from 'date-fns'
import { monthMapping } from '../utils/constant'

// Dữ liệu mẫu cho thống kê
const currentDate = new Date()
const currentMonth =
  monthMapping[currentDate.toLocaleString('default', { month: 'long' })]
const Dashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [summaryMode, setSummaryMode] = useState<'month' | 'year' | 'lastYear'>(
    'month'
  )
  const [revenueStatsState, setRevenueStatsState] = useState<RevenueStats | null>(
    null
  )
  const [statsData, setStatsData] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    lowStockItems: 0,
    revenueChange: 0,
    ordersChange: 0,
    averageOrderValueChange: 0
  })
  const [revenueData, setRevenueData] = useState<{
    labels: string[]
    currentPeriod: number[]
    previousPeriod: number[]
  }>({
    labels: [],
    currentPeriod: [],
    previousPeriod: []
  })
  const [topProducts, setTopProducts] = useState<
    {
      id: string
      name: string
      image?: string | null
      sales: number
      revenue: number
      deleted?: boolean
    }[]
  >([])
  const [orderStatusData, setOrderStatusData] = useState<
    {
      status: OrderStatus
      count: number
    }[]
  >([])
  const [recentOrders, setRecentOrders] = useState<OrderResponse[]>([])

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true)

        const [
          revenueStats,
          dailyRevenue,
          topSelling,
          orderStatsByStatus,
          ordersResponse
        ] = await Promise.all([
          revenueService.getRevenueStats(undefined),
          revenueService.getDailyRevenue(undefined),
          revenueService.getTopSelling(),
          orderService.getOrderStats(),
          orderService.getOrders({ page: 1, limit: 5 })
        ])

        const typedRevenueStats = revenueStats as RevenueStats
        setRevenueStatsState(typedRevenueStats)

        const initialStats =
          typedRevenueStats.thisMonth || typedRevenueStats.allTime

        setStatsData({
          totalRevenue: initialStats.totalRevenue,
          totalOrders: initialStats.orderCount,
          averageOrderValue: initialStats.averageOrderValue,
          lowStockItems:
            typedRevenueStats.lowStockItems ??
            typedRevenueStats.activeProducts ??
            0,
          revenueChange: 0,
          ordersChange: 0,
          averageOrderValueChange: 0
        })

        const labels = (dailyRevenue as DailyRevenue[]).map(item =>
          format(new Date(item.date), 'dd/MM')
        )
        const currentPeriod = (dailyRevenue as DailyRevenue[]).map(
          item => item.totalRevenue
        )

        setRevenueData({
          labels,
          currentPeriod,
          previousPeriod: []
        })

        const mappedTopProducts = (topSelling as TopSellingItem[]).map(
          item => ({
            id: item._id,
            name: item.name,
            image: item.image ?? null,
            sales: item.totalQuantity,
            revenue: item.totalRevenue,
            deleted: item.deleted ?? false
          })
        )

        setTopProducts(mappedTopProducts)

        const statusArray: {
          status: OrderStatus
          count: number
        }[] = Object.entries(orderStatsByStatus).map(([key, value]) => ({
          status: key as OrderStatus,
          count: value as number
        }))

        setOrderStatusData(statusArray)

        setRecentOrders(ordersResponse?.orders ?? [])
      } catch (error) {
        console.error('Failed to load dashboard data', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  useEffect(() => {
    if (!revenueStatsState) return

    let currentStats = revenueStatsState.allTime

    if (summaryMode === 'month') {
      currentStats = revenueStatsState.thisMonth || revenueStatsState.allTime
    } else if (summaryMode === 'year') {
      currentStats = revenueStatsState.thisYear || revenueStatsState.allTime
    } else if (summaryMode === 'lastYear') {
      currentStats = revenueStatsState.lastYear || revenueStatsState.allTime
    }

    setStatsData(prev => ({
      ...prev,
      totalRevenue: currentStats.totalRevenue,
      totalOrders: currentStats.orderCount,
      averageOrderValue: currentStats.averageOrderValue
    }))
  }, [summaryMode, revenueStatsState])

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <LoadingSpinner type='border' size='lg' color='border-green-500' />
      </div>
    )
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <h1 className='text-2xl font-semibold text-gray-900'>
          Welcome to DeSilk Manager
        </h1>
      </div>

      <div className='flex items-center mb-4 space-x-3'>
        <button
          type='button'
          onClick={() => setSummaryMode('month')}
          className={`px-3 py-1 text-sm rounded-md border ${
            summaryMode === 'month'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          Tháng này
        </button>
        <button
          type='button'
          onClick={() => setSummaryMode('year')}
          className={`px-3 py-1 text-sm rounded-md border ${
            summaryMode === 'year'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          Năm nay
        </button>
        <button
          type='button'
          onClick={() => setSummaryMode('lastYear')}
          className={`px-3 py-1 text-sm rounded-md border ${
            summaryMode === 'lastYear'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          Năm ngoái
        </button>
      </div>

      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore – props type mismatch in tooling, runtime OK */}
      <DashboardStats
        totalRevenue={statsData.totalRevenue}
        totalOrders={statsData.totalOrders}
        averageOrderValue={statsData.averageOrderValue}
        lowStockItems={statsData.lowStockItems}
        dateRange={`${currentMonth}`}
        comparisonDateRange=''
        revenueChange={0}
        ordersChange={0}
        averageOrderValueChange={0}
      />

      <div className='grid grid-cols-1 gap-5 mt-6 lg:grid-cols-12'>
        <div className='lg:col-span-7'>
          <RevenueChart
            title='Doanh thu 7 ngày gần nhất'
            labels={revenueData.labels}
            currentPeriodData={revenueData.currentPeriod}
            previousPeriodData={revenueData.previousPeriod}
            showPreviousPeriod={false}
          />
        </div>
        <div className='lg:col-span-5'>
          <OrderStatusChart
            title='Đơn hàng theo trạng thái'
            data={orderStatusData}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 gap-5 mt-6 lg:grid-cols-2'>
        <TopProducts
          title='Top sản phẩm bán chạy'
          products={topProducts}
          period={`${currentMonth}`}
        />

        <div className='p-5 bg-white rounded-lg shadow'>
          <h2 className='mb-4 text-lg font-semibold'>Đơn hàng gần đây</h2>
          {recentOrders.length === 0 ? (
            <div className='py-10 text-center text-gray-500'>
              Chưa có dữ liệu để hiển thị
            </div>
          ) : (
            <div className='space-y-3'>
              {recentOrders.map((order) => (
                <div
                  key={order._id}
                  className='flex justify-between items-center py-2 border-b border-gray-100 last:border-0'
                >
                  <div>
                    <p className='font-medium text-gray-900'>#{order.orderCode}</p>
                    <p className='text-sm text-gray-500'>
                      {order.fullName} · {order.phone}
                    </p>
                  </div>
                  <div className='text-right'>
                    <p className='font-semibold text-gray-900'>
                      {new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                        minimumFractionDigits: 0
                      }).format(order.priceDetail.totalPrice)}
                    </p>
                    <p className='text-xs text-gray-500'>
                      {format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
